import { useMemo } from 'react';
import type { IMessage } from 'react-native-gifted-chat';

import {
  getMessageReference,
  shouldRenderReferenceForViewer,
  type MessageWithOptionalReference,
} from '@/components/chatHelpers';
import { MESSAGE_DATA_TYPES } from '@/types/messages/discriminators';
import { isSupportMessageVisibleTo } from '@/types/messages/supportMessage';
import { isSameUserId } from '@/utils/isSameUserId';

type MessagePage = {
  messages: IMessage[];
};

type UseChatMessagesParams = {
  data: { pages: MessagePage[] } | undefined;
  currentUserId: string;
  /** Whether the viewer is the inquiry's submitter rather than its recipient. */
  isSubmitter: boolean;
};

/**
 * Deduplicates fetched chat messages and filters viewer-specific visibility.
 *
 * Reference request messages are kept in the list — `renderMessage` in
 * ChatContent applies the strategy pattern to render the appropriate bubble
 * based on the viewer's role (submitter vs recipient).
 *
 * Draft messages are hidden from the recipient but remain visible to the
 * author, so the author can review what they wrote before it's released.
 *
 * A support message addressed to one participant is dropped for the other. It
 * is filtered out here rather than hidden at render because the list length is
 * load-bearing: the expert's first-message prompt fires on a thread of one
 * message, and a hidden row still counts. The gate is cosmetic either way —
 * both participants' clients receive every body from Twilio.
 */
export function useChatMessages({ data, currentUserId, isSubmitter }: UseChatMessagesParams) {
  return useMemo(() => {
    if (!data) {
      return [];
    }

    const unique = [
      ...new Map(
        data.pages.flatMap((item) => item.messages).map((item) => [item._id, item])
      ).values(),
    ];

    return unique.filter((message) => {
      const typedMessage = message as MessageWithOptionalReference;

      if (typedMessage.draft && !isSameUserId(typedMessage.user?._id, currentUserId)) {
        return false;
      }

      if (typedMessage.type === 'reference' && getMessageReference(typedMessage)) {
        return shouldRenderReferenceForViewer(typedMessage, currentUserId);
      }

      if (typedMessage.type === MESSAGE_DATA_TYPES.SUPPORT_MESSAGE) {
        return isSupportMessageVisibleTo(typedMessage, {
          userId: currentUserId,
          isSubmitter,
        });
      }

      return true;
    });
  }, [data?.pages, currentUserId, isSubmitter]);
}
